import React from "react";
import {
  Drawer, Box, Typography, IconButton, Divider, Button, CircularProgress, Alert
} from "@mui/material";
import { Close } from "@mui/icons-material";

export default function PaymentSheet({
  open,
  payUrl,
  message,
  checking,
  iframeError,
  onManualCheck,
  onCancelOrder,
  onClose,            // optional; we’ll hide the close button if not provided
}) {
  const paidBtnRef = React.useRef(null);

  React.useEffect(() => {
    if (open && paidBtnRef.current) {
      // move immediate focus to the primary action
      paidBtnRef.current.focus();
    }
  }, [open]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose || (() => {})}
      PaperProps={{ sx: { height: '96vh', borderTopLeftRadius: 16, borderTopRightRadius: 16 } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flexGrow: 1 }}>
          Pay order
        </Typography>
        {onClose && (
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        )}
      </Box>
      <Divider />

      {/* Body */}
      <Box sx={{ px: 2, py: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Info / status line */}
        {message ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, minHeight: 24 }}
          >
            {message}
          </Typography>
        ) : <Box sx={{ minHeight: 24, mb: 1 }} />}

        {/* Payment iframe */}
        <Box sx={{
          flex: 1,
          minHeight: 300,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          {!iframeError ? (
            <iframe
              title="Pay online"
              src={payUrl}
              style={{ width: '100%', height: '100%', border: 0 }}
              sandbox="allow-forms allow-scripts allow-same-origin allow-popups"
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Alert severity="warning" variant="outlined">
                Couldn't load payment page.
              </Alert>
            </Box>
          )}
        </Box>

        {/* Sticky actions */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            pt: 1.25,
            pb: 1.5,
            bgcolor: 'background.paper'
          }}
        >
          {/* Small helper line with spinner when checking */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {checking ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Checking payment status…
                </Typography>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                After yoy confirm the payment, press <strong>„Paid”</strong>.
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {/* Primary: Am plătit (full width, prominent) */}
            <Button
              variant="contained"
              color="primary"
              onClick={onManualCheck}
              ref={paidBtnRef}
              size="large"
              sx={{ flexGrow: 1.5 }}   // ~60–70% width
            >
              {checking ? "Checking…" : "Paid"}
            </Button>

            {/* Secondary: Anulează (compact, text, error) */}
            <Button
              variant="text"
              color="error"
              onClick={onCancelOrder}
              sx={{ flexGrow: 0.5, whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}